import React from 'react';
import type { Candidate } from '@axiom/validation';
import {
  Button,
  CondensedLarge,
  CondensedMedium,
  Flashy,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ParagraphHeader,
  SmallHeader,
  TaxonomyUtil,
  useApi,
  Well,
} from '@axiom/ui';
import { CountryCodesConst, TaxonomyConst } from '@axiom/const';
import { CurrencyUtil } from '@axiom/utils';

import { PermissionsApi } from '../../api/permissions-api';
import { EnvUtil } from '../../utils/env-util';
import { CandidateApi } from '../../api/candidate-api';

const { LawyerBusinessTitle } = TaxonomyConst;

export type CompensationModalProps = {
  candidate: Candidate;
  openCloseModal: () => void;
};

export const CompensationModal = ({
  candidate,
  openCloseModal,
}: CompensationModalProps) => {
  const [{ data: permissions }, { data: compensationData }] = useApi(
    PermissionsApi.readPermissions(candidate.id),
    CandidateApi.readCandidateCompensation(candidate.id)
  );

  const isLawyer = TaxonomyUtil.isLawyer(candidate.businessTitle);

  const workingHours = (() => {
    if (
      (candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.US &&
        candidate.businessTitle === LawyerBusinessTitle) ||
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.HK ||
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.SG
    ) {
      return 50;
    }
    return (candidate.homeOffice?.countryCode ===
      CountryCodesConst.CountryCodesAbbreviations.US &&
      candidate.businessTitle !== LawyerBusinessTitle) ||
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.CA ||
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.UK ||
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.AU
      ? 40
      : 0;
  })();

  const premiumCompText = ((): string | null => {
    if (
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.US ||
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.HK ||
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.SG
    ) {
      return 'Premium compensation is provided when engagement is either unreserved or hours are less than 20 per week.';
    }

    if (
      candidate.homeOffice?.countryCode ===
      CountryCodesConst.CountryCodesAbbreviations.CA
    ) {
      return 'Premium compensation is provided when engagement is either unreserved or hours are less than 16 per week.';
    }

    return candidate.homeOffice?.countryCode ===
      CountryCodesConst.CountryCodesAbbreviations.UK ||
      candidate.homeOffice?.countryCode ===
        CountryCodesConst.CountryCodesAbbreviations.AU
      ? 'Premium compensation is provided for only unreserved engagements.'
      : null;
  })();

  const showPremium =
    isLawyer && premiumCompText && compensationData.compHourlyPremium;

  return (
    <Modal name="COMPENSATION_MODAL">
      <ModalHeader
        onClose={() => {
          openCloseModal();
        }}
      >
        Compensation
      </ModalHeader>
      <ModalSection>
        <CondensedLarge>
          These details reflect your current compensation agreement.
        </CondensedLarge>
        <Gutter bottom="16px" />
        <Well name="BASE_COMPENSATION" background="blue">
          {compensationData.compensation && compensationData.currencyType ? (
            <>
              <SmallHeader>
                Base annual compensation{' '}
                {CurrencyUtil.getFormattedCurrency({
                  value: compensationData.compensation,
                  currencyCode: compensationData.currencyType,
                  removeCentsIfZero: false,
                })}
              </SmallHeader>
              <Gutter bottom="16px" />
            </>
          ) : null}

          <CondensedMedium>
            This value is calculated assuming full-time 12-month employment at{' '}
            {workingHours} hours per week.
          </CondensedMedium>
        </Well>
        <Gutter bottom="16px" />
        <Grid columns={showPremium ? 2 : 1}>
          <GridRow stretched>
            <GridColumn stretched>
              <Well name="STANDARD_COMPENSATION">
                <ParagraphHeader>Standard Compensation</ParagraphHeader>
                <Gutter bottom="8px" />
                <CondensedLarge>
                  Your standard hourly rate is based on a {workingHours} hour
                  work week.
                </CondensedLarge>
                {compensationData.hourlyCompensation ? (
                  <Gutter top="8px">
                    <CondensedLarge>
                      <Flashy bold>Standard hourly rate:</Flashy>{' '}
                      {CurrencyUtil.getFormattedCurrency({
                        value: compensationData.hourlyCompensation,
                        currencyCode: compensationData.currencyType,
                        removeCentsIfZero: false,
                      })}
                    </CondensedLarge>
                  </Gutter>
                ) : null}
              </Well>
            </GridColumn>
            {isLawyer &&
            premiumCompText &&
            compensationData.compHourlyPremium ? (
              <GridColumn>
                <Well name="PREMIUM_COMPENSATION">
                  <ParagraphHeader>Premium Compensation</ParagraphHeader>
                  <Gutter bottom="16px" />
                  <CondensedLarge>{premiumCompText}</CondensedLarge>
                  <Gutter bottom="16px" />
                  <CondensedLarge>
                    <Flashy bold>Premium hourly rate:</Flashy>{' '}
                    {CurrencyUtil.getFormattedCurrency({
                      value: compensationData.compHourlyPremium,
                      currencyCode: compensationData.currencyType,
                      removeCentsIfZero: false,
                    })}
                  </CondensedLarge>
                </Well>
              </GridColumn>
            ) : null}
          </GridRow>
        </Grid>
        {permissions.workday ? (
          <Gutter top="16px">
            <CondensedLarge name="WORKDAY_SECTION">
              To access additional resources such as previous paystubs and tax
              documents please see{' '}
              <a
                href={`${EnvUtil.apiPortalUrl}/saml/workday`}
                target="_blank"
                rel="noreferrer"
              >
                Workday
              </a>
              .
            </CondensedLarge>
          </Gutter>
        ) : null}
      </ModalSection>
      <ModalFooter>
        <Button
          name="CLOSE"
          variation="outline"
          pattern="primary"
          onClick={() => {
            openCloseModal();
          }}
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
